import React, { useState, useRef, useCallback, useEffect } from 'react';
import { DocumentTree, DocumentTreeExpandSignal } from './DocumentTree';
import { DocumentDto, DocumentNode } from '../../types/documents';

interface ResizableDocumentTreeProps {
  treeData: DocumentNode | null;
  onNodeSelect: (node: DocumentNode) => void;
  selectedNode: DocumentNode | null;
  onExpandNode?: (node: DocumentNode) => void | Promise<void>;
  onDeleteNode?: (node: DocumentNode) => void;
  onEditDocument?: (node: DocumentNode) => void;
  onNodeRenamed?: (updated: DocumentDto) => void;
  onNodeMoved?: () => void;
  onDuplicateDocument?: (nodeId: string) => Promise<void>;
  treeExpandSignal?: DocumentTreeExpandSignal;
}

export const ResizableDocumentTree: React.FC<ResizableDocumentTreeProps> = ({
  treeData,
  onNodeSelect,
  selectedNode,
  onExpandNode,
  onDeleteNode,
  onEditDocument,
  onNodeRenamed,
  onNodeMoved,
  onDuplicateDocument,
  treeExpandSignal,
}) => {
  // Carregar largura salva do localStorage ou usar padrão
  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem('document-tree-width');
    return saved ? parseInt(saved, 10) : 320; // 320px = w-80
  });
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  const MIN_WIDTH = 200;
  const MAX_WIDTH = 600;

  // Salvar largura no localStorage quando mudar
  useEffect(() => {
    localStorage.setItem('document-tree-width', width.toString());
  }, [width]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
  }, [width]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = e.clientX - startXRef.current;
    const newWidth = startWidthRef.current + deltaX;
    
    // Limitar largura entre MIN_WIDTH e MAX_WIDTH
    const constrainedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
    setWidth(constrainedWidth);
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <div 
      ref={containerRef}
      className="bg-white rounded-lg shadow-sm flex"
      style={{ width: `${width}px` }}
    >
      {/* Árvore de documentos */}
      <div className="flex-1 overflow-hidden">
        <DocumentTree
          treeData={treeData}
          onNodeSelect={onNodeSelect}
          selectedNode={selectedNode}
          onExpandNode={onExpandNode}
          onDeleteNode={onDeleteNode}
          onEditDocument={onEditDocument}
          onNodeRenamed={onNodeRenamed}
          onNodeMoved={onNodeMoved}
          onDuplicateDocument={onDuplicateDocument}
          treeExpandSignal={treeExpandSignal}
        />
      </div>

      {/* Handle de redimensionamento */}
      <div
        className="w-2 bg-gray-100 hover:bg-blue-100 cursor-col-resize flex items-center justify-center group transition-colors relative"
        onMouseDown={handleMouseDown}
        title="Arraste para redimensionar"
      >
        {/* Indicador visual */}
        <div className="flex flex-col space-y-1">
          <div className="w-0.5 h-1 bg-gray-400 group-hover:bg-blue-500 transition-colors rounded-full" />
          <div className="w-0.5 h-1 bg-gray-400 group-hover:bg-blue-500 transition-colors rounded-full" />
          <div className="w-0.5 h-1 bg-gray-400 group-hover:bg-blue-500 transition-colors rounded-full" />
        </div>
        
        {/* Feedback visual durante redimensionamento */}
        {isResizing && (
          <div className="absolute inset-0 bg-blue-200 opacity-50" />
        )}
      </div>
    </div>
  );
};
